import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, ChevronDown, ChevronUp, Check } from 'react-feather';
import * as staffAPI from '../services/staffAPI';
import type { NoteReply } from '../services/staffAPI';
import { useAuth } from '../contexts/AuthContext';
import NoteReplyThread, { DEFAULT_REPLY_MAX_LENGTH } from './NoteReplyThread';
import { noteCategoryLabel } from '../utils/noteCategories';

interface CoachNote {
  id: string;
  author_name: string;
  note_text: string;
  category: string;
  is_read: boolean;
  read_at: string | null;
  team_name?: string;
  org_name?: string;
  created_at: string;
  // APS-253: present only when the server sends replies_enabled.
  replies?: NoteReply[];
  can_reply?: boolean;
}

const CoachNotesSection: React.FC = () => {
  const { sessionToken } = useAuth();
  const { hash } = useLocation();
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [hasTeamMembership, setHasTeamMembership] = useState(false);
  const [repliesEnabled, setRepliesEnabled] = useState(false);
  const [replyMaxLength, setReplyMaxLength] = useState(DEFAULT_REPLY_MAX_LENGTH);

  const fetchNotes = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const data = await staffAPI.getMyNotes();
      setNotes(data.notes || []);
      setUnreadCount(data.unread || 0);
      setHasTeamMembership(data.hasTeamMembership !== false);
      setRepliesEnabled(data.replies_enabled === true);
      if (typeof data.reply_max_length === 'number') setReplyMaxLength(data.reply_max_length);
    } catch (error) {
      console.error('Failed to load coach notes:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleMarkRead = async (noteId: string) => {
    try {
      await staffAPI.markNoteRead(noteId);
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark note as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await staffAPI.markAllNotesRead();
      setNotes(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notes as read:', error);
    }
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const categoryColors: Record<string, { bg: string; color: string }> = {
    general: { bg: '#f3f4f6', color: '#374151' },
    suggestion: { bg: '#dbeafe', color: '#1e40af' },
    feedback: { bg: '#fef3c7', color: '#92400e' },
    action_item: { bg: '#fee2e2', color: '#991b1b' }
  };

  // Note emails and alerts link to /dashboard#coach-notes: open the section and scroll to it.
  useEffect(() => {
    if (loading || notes.length === 0 || hash !== '#coach-notes') return;
    setExpanded(true);
    requestAnimationFrame(() => {
      document.getElementById('coach-notes')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }, [loading, notes.length, hash]);

  if (loading) return null;

  // Don't show the section if athlete isn't on any team
  if (!hasTeamMembership) return null;

  // Hide entirely when there are no notes
  if (notes.length === 0) return null;

  return (
    <div id="coach-notes" style={{
      background: '#fff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '20px',
      overflow: 'hidden',
      scrollMarginTop: 16
    }}>
      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 20px',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid #f0f0f0' : 'none',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MessageSquare size={18} color="#f9550d" />
          <span style={{ fontWeight: '600', fontSize: '15px', color: '#151133' }}>
            Notes from Your Coaches
          </span>
          {unreadCount > 0 && (
            <span style={{
              background: '#f9550d', color: '#fff', fontSize: '11px', fontWeight: '700',
              padding: '2px 8px', borderRadius: '10px', minWidth: '20px', textAlign: 'center'
            }}>
              {unreadCount} new
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {unreadCount > 0 && expanded && (
            <button
              onClick={(e) => { e.stopPropagation(); handleMarkAllRead(); }}
              style={{
                background: 'none', border: 'none', color: '#f9550d', fontSize: '12px',
                fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
                padding: '4px 8px'
              }}
            >
              Mark All Read
            </button>
          )}
          {expanded ? <ChevronUp size={18} color="#6c757d" /> : <ChevronDown size={18} color="#6c757d" />}
        </div>
      </div>

      {/* Notes List */}
      {expanded && (
        <div style={{ maxHeight: repliesEnabled ? '520px' : '300px', overflowY: 'auto' }}>
          {notes.map((note) => {
            const catStyle = categoryColors[note.category] || categoryColors.general;
            const showThread = repliesEnabled && Array.isArray(note.replies);
            return (
              <div
                key={note.id}
                style={{
                  borderBottom: '1px solid #f8f8f8',
                  background: note.is_read ? 'transparent' : '#fefaf8'
                }}
              >
              <div
                role={!note.is_read ? 'button' : undefined}
                tabIndex={!note.is_read ? 0 : undefined}
                aria-label={!note.is_read ? 'Mark note as read' : undefined}
                onClick={() => !note.is_read && handleMarkRead(note.id)}
                onKeyDown={
                  !note.is_read
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMarkRead(note.id);
                        }
                      }
                    : undefined
                }
                style={{
                  padding: '12px 20px',
                  cursor: note.is_read ? 'default' : 'pointer',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start'
                }}
              >
                {/* Unread indicator */}
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px',
                  background: note.is_read ? 'transparent' : '#f9550d', flexShrink: 0
                }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Top row: author + category + time */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#151133' }}>
                      {note.author_name}
                    </span>
                    {note.team_name && (
                      <span style={{ fontSize: '11px', color: '#6c757d' }}>· {note.team_name}</span>
                    )}
                    <span style={{
                      padding: '1px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '500',
                      background: catStyle.bg, color: catStyle.color
                    }}>
                      {noteCategoryLabel(note.category)}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>
                      {timeAgo(note.created_at)}
                    </span>
                  </div>
                  {/* Note text */}
                  <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
                    {note.note_text}
                  </p>
                </div>

                {/* Read indicator */}
                {note.is_read && (
                  <Check size={14} color="#10B981" style={{ marginTop: '6px', flexShrink: 0 }} />
                )}
              </div>
              {showThread && (
                <div style={{ padding: '0 20px 12px 40px' }}>
                  <NoteReplyThread
                    noteId={note.id}
                    replies={note.replies || []}
                    canReply={note.can_reply !== false}
                    maxLength={replyMaxLength}
                    send={staffAPI.createMyNoteReply}
                    remove={staffAPI.deleteMyNoteReply}
                    onChange={(replies) => setNotes(prev => prev.map(n => n.id === note.id ? { ...n, replies } : n))}
                    onOpen={() => { if (!note.is_read) handleMarkRead(note.id); }}
                  />
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachNotesSection;
